import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from './Footer';
import { motion } from 'framer-motion';
import Navbar from './Navbar';
import { useUser, SignInButton } from '@clerk/clerk-react';
import { u } from 'framer-motion/client';

// Default to sandbox for safety

export default function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();


  
  const [packageInfo, setPackageInfo] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [error, setError] = useState(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('paypal'); // Default to PayPal
  
  // Form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  
  // PayPal script loaded state
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  

  
  // Get package information from URL parameters
  useEffect(() => {
    if (!user) return;
    const searchParams = new URLSearchParams(location.search);
    const packageId = searchParams.get('package');
    const price = searchParams.get('price');
    const name = searchParams.get('name');
    
    if (packageId && price && name) {
      setPackageInfo({
        id: packageId,
        price,
        name
      });
    } else {
      setError('Missing package information. Please select a package first.');
    }
    
    // Set page as loaded after a short delay for entrance animation
    setTimeout(() => {
      setPageLoaded(true);
    }, 300);
  }, [location.search, user]);
  
  // Load PayPal script
  useEffect(() => {
    if (!packageInfo || !user) return;
    
    // Use environment variables for PayPal configuration
    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
    // Opcional — garantir idioma/país
    // script.src += '&buyer-country=BR&locale=pt_BR';
    
    script.onload = () => setPaypalLoaded(true);
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, [packageInfo, user]);
  
  // Initialize PayPal buttons when script is loaded
  useEffect(() => {
    if (paypalLoaded && packageInfo && window.paypal && user) {
      try {
        // Clear any existing buttons
        const container = document.getElementById('paypal-button-container');
        if (container) container.innerHTML = '';
        
        window.paypal.Buttons({
          // Set up the transaction
          createOrder: function(data, actions) {
            return actions.order.create({
              purchase_units: [{
                description: `WizardPortrait ${packageInfo.name} Plan`,
                amount: {
                  currency_code: 'USD',
                  value: packageInfo.price
                }
              }]
            });
          },
          // Finalize the transaction
          onApprove: function(data, actions) {
            setIsProcessing(true);
            
            return actions.order.capture().then(function(details) {
              console.log('Payment completed', details);
              setIsPaymentComplete(true);
            }).catch(function(err) {
              console.error('Payment failed', err);
              setError('Payment failed. Please try again or use a different payment method.');
              setIsProcessing(false);
            });
          },
          // Handle errors
          onError: function(err) {
            console.error('PayPal error', err);
            setError('There was a problem with PayPal. Please try again or use a different payment method.');
          }
        }).render('#paypal-button-container');
      } catch (err) {
        console.error('Error rendering PayPal buttons', err);
        setError('There was a problem loading PayPal. Please try again or use a different payment method.');
      }
    }
  }, [paypalLoaded, packageInfo, user]);
  
  const handleCreditCardSubmit = async (e) => {
    e.preventDefault();
    if (!packageInfo || !user) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      // In a real implementation, you would process the credit card payment here
      // using a payment processor like Stripe
      
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate successful payment
      setIsPaymentComplete(true);
      
    } catch (err) {
      console.error("Payment error:", err);
      setError("Payment processing failed. Please check your payment details and try again.");
      setIsProcessing(false);
    }
  };
  
  // Show loading state if auth is being checked
  if (!user) {
    return (
      <div className="min-h-screen">
        <Navbar /> 
        <main className="payment-page">
          <div className="loading-container">
            <div className="loading-animation">
              <div className="loading-bar"></div>
              <div className="loading-bar"></div>
              <div className="loading-bar"></div>
            </div>
            <p>Please sign in to continue</p>
            <SignInButton mode="modal" className="clerk-button sign-in">
              <button className="clerk-button sign-in">
                Sign In
              </button>
            </SignInButton>
          </div>
        </main>
        <Footer />
        <style jsx>{`
          .payment-page {
            background: var(--light, #f9f9f9);
            color: var(--text-dark, #333);
            padding: 8rem 2rem;
            min-height: 80vh;
          }
          
          .loading-container {
            max-width: 800px;
            margin: 0 auto;
            text-align: center;
          }
          
          .loading-animation {
            padding: 3rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          }
          
          .loading-bar {
            height: 1rem;
            background-color: rgba(106, 62, 161, 0.2);
            border-radius: 0.5rem;
            margin-bottom: 0.5rem;
            animation: pulse 1.5s infinite;
          }
          
          .loading-bar:nth-child(1) {
            width: 25%;
          }
          
          .loading-bar:nth-child(2) {
            width: 50%;
          }
          
          .loading-bar:nth-child(3) {
            width: 33%;
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 0.5;
            }
            50% {
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  }
  
  // Show loading state while getting package info
  if (!packageInfo && !error) {
    return (
      <div className="min-h-screen">
        <Navbar /> 
        <main className="payment-page">
          <div className="loading-container">
            <div className="loading-animation">
              <div className="loading-bar"></div>
              <div className="loading-bar"></div>
              <div className="loading-bar"></div>
            </div>
          </div>
        </main>
        
        <style jsx>{`
          .payment-page {
            background: var(--light, #f9f9f9);
            color: var(--text-dark, #333);
            padding: 8rem 2rem;
            min-height: 80vh;
          }
          
          .loading-container {
            max-width: 800px;
            margin: 0 auto;
            text-align: center;
          }
          
          .loading-animation {
            padding: 3rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          }
          
          .loading-bar {
            height: 1rem;
            background-color: rgba(106, 62, 161, 0.2);
            border-radius: 0.5rem;
            margin-bottom: 0.5rem;
            animation: pulse 1.5s infinite;
          }
          
          .loading-bar:nth-child(1) {
            width: 25%;
          }
          
          .loading-bar:nth-child(2) {
            width: 50%;
          }
          
          .loading-bar:nth-child(3) {
            width: 33%;
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 0.5;
            }
            50% {
              opacity: 1;
            }
          }
        `}</style>
        
        <Footer />
      </div>
    );
  }
  
  // Show error if package info is missing
  if (error && !isPaymentComplete) {
    return (
      <div className="min-h-screen">
        <Navbar /> 
        <main className="payment-page">
          <div className="error-container">
            <h2 className="error-title">Error</h2>
            <p className="error-message">{error}</p>
            <button
              onClick={() => navigate('/pricing')}
              className="return-button"
            >
              Return to Pricing
            </button>
          </div>
        </main>
        
        <style jsx>{`
          .payment-page {
            background: var(--light, #f9f9f9);
            color: var(--text-dark, #333);
            padding: 8rem 2rem;
            min-height: 80vh;
            text-align: center;
          }
          
          .error-container {
            max-width: 600px;
            margin: 0 auto;
            background-color: #fef2f2;
            border-radius: 1rem;
            padding: 2rem;
            color: #b91c1c;
          }
          
          .error-title {
            font-family: var(--serif, serif);
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
          }
          
          .error-message {
            margin-bottom: 1.5rem;
          }
          
          .return-button {
            background: var(--primary, #6A3EA1);
            color: white;
            font-size: 1rem;
            font-weight: 600;
            padding: 0.8rem 1.8rem;
            border-radius: 50px;
            border: none;
            cursor: pointer;
            transition: all 0.3s ease;
            display: inline-block;
          }
          
          .return-button:hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 20px rgba(106, 62, 161, 0.3);
          }
        `}</style>
        
        <Footer />
      </div>
    );
  }
  
  // Show success screen after payment is complete
  if (isPaymentComplete) {
    return (
      <div className="min-h-screen">
        <Navbar /> 
        <main className="payment-page">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="success-container"
          >
            <div className="success-icon">✓</div>
            <h1 className="success-title">Payment Successful!</h1>
            <p className="success-message">
              Thank you for your purchase of the WizardPortrait {packageInfo.name} plan.
              Your magical portrait experience will begin shortly.
            </p>
            
            <div className="order-details">
              <h2 className="details-title">Order Details</h2>
              <div className="detail-row">
                <span>Package:</span>
                <span>{packageInfo.name}</span>
              </div>
              <div className="detail-row">
                <span>Amount:</span>
                <span>${packageInfo.price}/month, billed annually</span>
              </div>
              <div className="detail-row">
                <span>Order ID:</span>
                <span>WP-{Math.random().toString(36).substring(2, 10).toUpperCase()}</span>
              </div>
              <div className="detail-row">
                <span>Payment Method:</span>
                <span>{paymentMethod === 'paypal' ? 'PayPal' : 'Credit Card'}</span>
              </div>
            </div>
            
            <div className="action-buttons">
              <button 
                onClick={() => navigate('/dashboard')}
                className="primary-button"
              >
                Go to Dashboard
              </button>
              <button 
                onClick={() => navigate('/')}
                className="secondary-button"
              >
                Return to Home
              </button>
            </div>
          </motion.div>
        </main>
        
        <style jsx>{`
          .payment-page {
            background: var(--light, #f9f9f9);
            color: var(--text-dark, #333);
            padding: 8rem 2rem;
            min-height: 80vh;
            display: flex;
            justify-content: center;
            align-items: flex-start;
          }
          
          .success-container {
            max-width: 600px;
            width: 100%;
            background: white;
            border-radius: 15px;
            padding: 3rem 2rem;
            text-align: center;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          }
          
          .success-icon {
            width: 80px;
            height: 80px;
            background: var(--primary, #6A3EA1);
            color: white;
            font-size: 2.5rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1.5rem;
          }
          
          .success-title {
            font-family: var(--serif, serif);
            font-size: 2rem;
            margin-bottom: 1rem;
            color: var(--primary, #6A3EA1);
          }
          
          .success-message {
            color: var(--text-muted, #666);
            margin-bottom: 2rem;
          }
          
          .order-details {
            background: #f9fafb;
            border-radius: 0.5rem;
            padding: 1.5rem;
            margin-bottom: 2rem;
            text-align: left;
          }
          
          .details-title {
            font-size: 1.2rem;
            font-weight: 600;
            margin-bottom: 1rem;
          }
          
          .detail-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 0.5rem;
            font-size: 0.95rem;
          }
          
          .detail-row span:first-child {
            color: var(--text-muted, #666);
          }
          
          .action-buttons {
            display: flex;
            gap: 1rem;
            justify-content: center;
          }
          
          .primary-button, .secondary-button {
            padding: 0.8rem 1.8rem;
            border-radius: 50px;
            font-weight: 600;
            transition: all 0.3s ease;
            cursor: pointer;
          }
          
          .primary-button {
            background: var(--accent, #F9B932);
            color: var(--text-dark, #333);
            border: none;
          }
          
          .primary-button:hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 20px rgba(249, 185, 50, 0.3);
          }
          
          .secondary-button {
            background: transparent;
            color: var(--primary, #6A3EA1);
            border: 1px solid var(--primary, #6A3EA1);
          }
          
          .secondary-button:hover {
            background: rgba(106, 62, 161, 0.05);
          }
          
          @media (max-width: 768px) {
            .action-buttons {
              flex-direction: column;
            }
          }
        `}</style>
        
        <Footer />
      </div>
    );
  }
  
  // Main payment form
  return (
    <div className="min-h-screen">
      <Navbar /> 
      <main className="payment-page">
        {user && (
          <div className="user-greeting">
            <p>Hello, {user.firstName || user.username || 'Wizard'}! You're about to make a purchase.</p>
          </div>
        )}
        
        <div>
          {/* Progress indicators */}
          <div className="progress-container">
            <div className="progress-steps">
              <div className="progress-step">
                <div className="step-circle completed">1</div>
                <span className="step-label">Choose Plan</span>
              </div>
              <div className="progress-line active"></div>
              <div className="progress-step">
                <div className="step-circle completed">2</div>
                <span className="step-label">Review</span>
              </div>
              <div className="progress-line active"></div>
              <div className="progress-step">
                <div className="step-circle active">3</div>
                <span className="step-label">Payment</span>
              </div>
            </div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: pageLoaded ? 1 : 0, y: pageLoaded ? 0 : 20 }}
            transition={{ duration: 0.5 }}
            className="payment-header"
          >
            <h1 className="section-title">Complete Your Purchase</h1>
            <p className="subtitle">
              Choose your payment method
            </p>
          </motion.div>
          
          <div className="payment-layout">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: pageLoaded ? 1 : 0, y: pageLoaded ? 0 : 30 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="payment-form-container"
            >
              {error && (
                <div className="payment-error">
                  {error}
                </div>
              )}
              
              {/* Payment method selector */}
              <div className="payment-methods">
                <div 
                  className={`payment-method ${paymentMethod === 'paypal' ? 'selected' : ''}`}
                  onClick={() => setPaymentMethod('paypal')}
                >
                  <div className="method-icon paypal-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                      <path d="M7 10C5.9 8.9 5.9 7.1 7 6C8.1 4.9 9.9 4.9 11 6L17 12C18.1 13.1 18.1 14.9 17 16C15.9 17.1 14.1 17.1 13 16L10 13" />
                    </svg>
                  </div>
                  <div className="method-info">
                    <span className="method-name">PayPal</span>
                    <span className="method-desc">Pay securely via PayPal</span>
                  </div>
                </div>
                
                <div 
                  className={`payment-method ${paymentMethod === 'card' ? 'selected' : ''}`}
                  onClick={() => setPaymentMethod('card')}
                >
                  <div className="method-icon card-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                      <rect x="2" y="5" width="20" height="14" rx="2" />
                      <line x1="2" y1="10" x2="22" y2="10" />
                    </svg>
                  </div>
                  <div className="method-info">
                    <span className="method-name">Credit Card</span>
                    <span className="method-desc">Pay with your credit card</span>
                  </div>
                </div>
              </div>
              
              {/* PayPal payment option */}
              {paymentMethod === 'paypal' && (
                <div className="paypal-container">
                  <div className="paypal-message">
                    <p>Click the PayPal button below to complete your purchase</p>
                  </div>
                  <div id="paypal-button-container" className="paypal-buttons"></div>
                  {!paypalLoaded && (
                    <div className="paypal-loading">
                      <div className="spinner"></div>
                      <p>Loading PayPal...</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Credit card payment option */}
              {paymentMethod === 'card' && (
                <form onSubmit={handleCreditCardSubmit} className="payment-form">
                  <div className="form-group">
                    <label htmlFor="cardName">Cardholder Name</label>
                    <input
                      id="cardName"
                      type="text"
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value)}
                      required
                      className="form-input"
                      placeholder="Full name on card"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="cardNumber">Card Number</label>
                    <input
                      id="cardNumber"
                      type="text"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))}
                      required
                      className="form-input"
                      placeholder="1234 5678 9012 3456"
                      pattern="[0-9]{16}"
                      maxLength={16}
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="expiryDate">Expiry Date</label>
                      <input
                        id="expiryDate"
                        type="text"
                        value={expiryDate}
                        onChange={(e) => {
                          const input = e.target.value.replace(/\D/g, '').slice(0, 4);
                          if (input.length > 2) {
                            setExpiryDate(`${input.slice(0, 2)}/${input.slice(2)}`);
                          } else {
                            setExpiryDate(input);
                          }
                        }}
                        required
                        className="form-input"
                        placeholder="MM/YY"
                        maxLength={5}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="cvv">CVV</label>
                      <input
                        id="cvv"
                        type="text"
                        value={cvv}
                        onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 3))}
                        required
                        className="form-input"
                        placeholder="123"
                        pattern="[0-9]{3}"
                        maxLength={3}
                      />
                    </div>
                  </div>
                  
                  <div className="action-container">
                    <button
                      type="submit"
                      disabled={isProcessing}
                      className={`payment-button ${isProcessing ? 'processing' : ''}`}
                    >
                      {isProcessing ? (
                        <div className="processing-container">
                          <div className="spinner"></div>
                          Processing Payment...
                        </div>
                      ) : (
                        `Pay $${packageInfo.price}`
                      )}
                    </button>
                  </div>
                </form>
              )}
              
              <div className="secure-payment">
                <div className="secure-icon">🔒</div>
                <p>Secured payment processing. Your payment information is encrypted.</p>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: pageLoaded ? 1 : 0, y: pageLoaded ? 0 : 30 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="order-summary-container"
            >
              <div className="order-summary">
                <h2 className="summary-title">Order Summary</h2>
                
                <div className="summary-item">
                  <div className="item-info">
                    <h3>{packageInfo.name} Plan</h3>
                    <p>Annual subscription, billed monthly</p>
                  </div>
                  <div className="item-price">
                    ${packageInfo.price}/mo
                  </div>
                </div>
                
                <div className="summary-divider"></div>
                
                <div className="summary-total">
                  <span>Total billed monthly</span>
                  <span>${packageInfo.price}</span>
                </div>
                
                <div className="summary-annual">
                  <span>Annual total</span>
                  <span>${packageInfo.price * 12}</span>
                </div>
              </div>
              
              <div className="back-link-container">
                <button
                  onClick={() => navigate('/checkout' + location.search)}
                  className="back-link"
                >
                  &larr; Back to review
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
      
      <style jsx>{`
        .payment-page {
          background: var(--light, #f9f9f9);
          color: var(--text-dark, #333);
          padding: 8rem 2rem;
          text-align: center;
        }
        
        .user-greeting {
          margin-bottom: 2rem;
          color: var(--primary, #6A3EA1);
          font-weight: 600;
        }
        
        .section-title {
          font-family: var(--serif, serif);
          font-size: 2.5rem;
          text-align: center;
          margin-bottom: 1rem;
          position: relative;
          display: inline-block;
          font-weight: 600;
        }
        
        .section-title::after {
          content: '';
          position: absolute;
          width: 80px;
          height: 4px;
          background: var(--accent, #F9B932);
          bottom: -15px;
          left: 50%;
          transform: translateX(-50%);
        }
        
        .subtitle {
          font-size: 1.2rem;
          max-width: 700px;
          margin: 2rem auto 3rem;
        }
        
        .progress-container {
          margin: 3rem auto;
          max-width: 600px;
        }
        
        .progress-steps {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .progress-step {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .step-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--primary, #6A3EA1);
          opacity: 0.5;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        
        .step-circle.active, .step-circle.completed {
          opacity: 1;
        }
        
        .step-label {
          margin-top: 8px;
          font-size: 0.9rem;
        }
        
        .progress-line {
          flex: 1;
          height: 2px;
          background: var(--secondary, #9857D3);
          opacity: 0.3;
          margin: 0 10px;
        }
        
        .progress-line.active {
          opacity: 1;
        }
        
        .payment-header {
          margin-bottom: 3rem;
        }
        
        .payment-layout {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .payment-form-container {
          background: white;
          border-radius: 15px;
          padding: 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          text-align: left;
        }
        
        .payment-error {
          background-color: #fef2f2;
          color: #b91c1c;
          padding: 0.75rem;
          border-radius: 0.5rem;
          margin-bottom: 1.5rem;
        }
        
        .payment-methods {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }
        
        .payment-method {
          display: flex;
          align-items: center;
          padding: 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .payment-method:hover {
          border-color: var(--primary, #6A3EA1);
        }
        
        .payment-method.selected {
          border-color: var(--primary, #6A3EA1);
          border-width: 2px;
          background: rgba(106, 62, 161, 0.05);
        }
        
        .method-icon {
          width: 44px;
          height: 44px;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          color: var(--primary, #6A3EA1);
          background: rgba(106, 62, 161, 0.1);
          flex-shrink: 0;
        }
        
        .paypal-icon {
          color: #0070ba;
          background: rgba(0, 112, 186, 0.1);
        }
        
        .method-info {
          display: flex;
          flex-direction: column;
        }
        
        .method-name {
          font-weight: 600;
        }
        
        .method-desc {
          font-size: 0.875rem;
          color: var(--text-muted, #666);
        }
        
        .paypal-container {
          margin-top: 1.5rem;
        }
        
        .paypal-message {
          margin-bottom: 1.5rem;
          color: var(--text-muted, #666);
        }
        
        .paypal-buttons {
          min-height: 40px;
        }
        
        .paypal-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          background: #f9fafb;
          border-radius: 0.5rem;
          color: var(--text-muted, #666);
        }
        
        .payment-form {
          margin-top: 1.5rem;
        }
        
        .form-group {
          margin-bottom: 1.5rem;
        }
        
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        
        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }
        
        .form-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          font-size: 1rem;
          transition: border-color 0.3s;
        }
        
        .form-input:focus {
          outline: none;
          border-color: var(--primary, #6A3EA1);
          box-shadow: 0 0 0 2px rgba(106, 62, 161, 0.2);
        }
        
        .action-container {
          margin-top: 2rem;
        }
        
        .payment-button {
          width: 100%;
          background: var(--accent, #F9B932);
          color: var(--text-dark, #333);
          font-size: 1rem;
          font-weight: 600;
          padding: 1rem 2rem;
          border-radius: 50px;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .payment-button:hover:not(.processing) {
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(249, 185, 50, 0.3);
        }
        
        .payment-button.processing {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .processing-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .spinner {
          width: 1.25rem;
          height: 1.25rem;
          border: 2px solid rgba(0, 0, 0, 0.3);
          border-top-color: var(--text-dark, #333);
          border-radius: 50%;
          margin-right: 0.75rem;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        
        .secure-payment {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: var(--text-muted, #666);
          font-size: 0.9rem;
          margin-top: 2rem;
        }
        
        .order-summary-container {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        
        .order-summary {
          background: white;
          border-radius: 15px;
          padding: 1.5rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          text-align: left;
        }
        
        .summary-title {
          font-family: var(--serif, serif);
          font-size: 1.3rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          color: var(--primary, #6A3EA1);
        }
        
        .summary-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1rem;
        }
        
        .item-info h3 {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }
        
        .item-info p {
          font-size: 0.9rem;
          color: var(--text-muted, #666);
        }
        
        .item-price {
          font-weight: 600;
        }
        
        .summary-divider {
          height: 1px;
          background: rgba(0, 0, 0, 0.1);
          margin: 1.5rem 0;
        }
        
        .summary-total {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        
        .summary-annual {
          display: flex;
          justify-content: space-between;
          color: var(--text-muted, #666);
          font-size: 0.9rem;
        }
        
        .back-link-container {
          margin-top: 1.5rem;
        }
        
        .back-link {
          color: var(--primary, #6A3EA1);
          background: none;
          border: none;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        
        .back-link:hover {
          color: var(--secondary, #9857D3);
        }
        
        @media (max-width: 768px) {
          .payment-layout {
            grid-template-columns: 1fr;
          }
          
          .section-title {
            font-size: 2rem;
          }
          
          .form-row {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
        }
      `}</style>
      
      <Footer />
    </div>
  );
} 